/**
 * Google Secure Cookies Management
 * 
 * This utility handles Google's secure cookies (__Secure-*) which are used
 * by Google services like Analytics, Maps, Fonts, etc.
 * 
 * These cookies are third-party cookies set by Google domains and require
 * user consent under GDPR/ePrivacy regulations.
 */

/**
 * List of Google secure cookies that may be set by Google services
 * These are documented by Google and commonly used across their services
 */
export const GOOGLE_SECURE_COOKIES = [
  // Authentication & Session Cookies
  '__Secure-3PAPISID',  // Google API authentication
  '__Secure-3PSID',     // Google session ID (third-party)
  '__Secure-3PSIDCC',   // Google session ID with consent check
  '__Secure-1PAPISID',  // Google API authentication (first-party)
  '__Secure-1PSID',     // Google session ID (first-party)
  '__Secure-1PSIDCC',   // Google session ID with consent check (first-party)
  '__Secure-1PSIDTS',   // Google session timestamp (first-party)
  '__Secure-3PSIDTS',   // Google session timestamp (third-party)
  
  // Analytics & Tracking Cookies
  '__Secure-ENID',      // Google Analytics/Ads
  '__Secure-HSID',     // Google account security
  '__Secure-SSID',     // Google account session
  '__Secure-APISID',   // Google API session
  '__Secure-SAPISID',  // Google secure API session
  '__Secure-SID',     // Google session
  '__Secure-SIDCC',   // Google session with consent check
  
  // Additional Google cookies
  'NID',              // Google preferences (non-secure, but commonly used)
  'OGPC',             // Google account preferences
  'OGP',              // Google account preferences
  '1P_JAR',           // Google personalization
  'AEC',              // Google account security
] as const;

/**
 * Cookie domains where Google cookies are typically set
 * Based on Google's CSP documentation and cookie standards
 */
export const GOOGLE_COOKIE_DOMAINS = [
  '.google.com',
  '.google.nl',
  '.googleapis.com',
  '.gstatic.com',
  '.google-analytics.com',
  '.googletagmanager.com',
  '.doubleclick.net',
  '.googleadservices.com',
  '.analytics.google.com',
  '.g.doubleclick.net',
  'pagead2.googlesyndication.com',
];

/**
 * Check if a cookie name is a Google cookie
 */
export function isGoogleCookie(cookieName: string): boolean {
  return GOOGLE_SECURE_COOKIES.some(name => 
    cookieName === name || 
    cookieName.startsWith('__Secure-') ||
    cookieName.startsWith('_ga') || // Google Analytics cookies
    cookieName.startsWith('_gid') ||
    cookieName.startsWith('_gat')
  );
}

/**
 * Get all Google cookies from the current domain
 * Note: Due to browser security, we can only access cookies for the current domain
 * Third-party cookies set by Google domains are not accessible via JavaScript
 */
export function getGoogleCookies(): Record<string, string> {
  if (typeof document === 'undefined') {
    return {};
  }

  const cookies: Record<string, string> = {};
  const allCookies = document.cookie.split(';');

  allCookies.forEach(cookie => {
    const [name, ...valueParts] = cookie.trim().split('=');
    const value = valueParts.join('=');
    
    if (name && isGoogleCookie(name)) {
      cookies[name] = value;
    }
  });

  return cookies;
}

/**
 * Clear Google cookies from the current domain
 * Note: This only clears cookies set on the current domain
 * Third-party cookies must be cleared by the user's browser settings
 * 
 * Following Google's standards for cookie handling:
 * - __Secure-* cookies require Secure flag and HTTPS
 * - Proper SameSite attributes based on cookie type
 * - Correct path and domain scoping
 */
export function clearGoogleCookies(): void {
  if (typeof document === 'undefined') {
    return;
  }

  const isSecure = window.location.protocol === 'https:';
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  
  // Clear cookies on current domain
  GOOGLE_SECURE_COOKIES.forEach(cookieName => {
    const isSecureCookie = cookieName.startsWith('__Secure-');
    const paths = ['/', window.location.pathname];
    const domains = isLocalhost 
      ? [hostname] 
      : [hostname, `.${hostname}`];

    paths.forEach(path => {
      domains.forEach(domain => {
        // For __Secure-* cookies, use Secure flag only on HTTPS
        // Use SameSite=None for third-party cookies, Lax for first-party
        const sameSite = isSecureCookie ? 'SameSite=None' : 'SameSite=Lax';
        const secureFlag = (isSecureCookie && isSecure) ? '; Secure' : '';
        
        // Clear with domain
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; ${sameSite}${secureFlag}`;
        // Clear without domain (for cookies set without domain attribute)
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; ${sameSite}${secureFlag}`;
      });
    });
  });

  // Clear Google Analytics cookies (first-party cookies, use SameSite=Lax)
  // According to Google Analytics documentation, these are first-party cookies
  const gaCookies = ['_ga', '_gid', '_gat', '_gat_gtag_', '_gcl_au', '_gcl_dc', '_gcl_gf', '_gcl_gw', '_gac', '_gac_gb'];
  gaCookies.forEach(cookieName => {
    const paths = ['/', window.location.pathname];
    const domains = isLocalhost 
      ? [hostname] 
      : [hostname, `.${hostname}`];
    
    paths.forEach(path => {
      domains.forEach(domain => {
        // GA cookies are first-party, use SameSite=Lax
        // Secure flag only on HTTPS
        const secureFlag = isSecure ? '; Secure' : '';
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain}; SameSite=Lax${secureFlag}`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; SameSite=Lax${secureFlag}`;
      });
    });
  });

  // Google cookies cleared from current domain
}

/**
 * Get information about Google cookies for display in cookie preferences
 */
export function getGoogleCookieInfo() {
  return {
    category: 'analytics' as const,
    cookies: GOOGLE_SECURE_COOKIES.map(name => ({
      name,
      purpose: getCookiePurpose(name),
      domain: 'google.com and related domains',
      duration: getCookieDuration(name),
      type: name.startsWith('__Secure-') ? 'Secure HTTP-only' : 'HTTP',
    })),
  };
}

/**
 * Get the purpose of a specific Google cookie
 */
function getCookiePurpose(cookieName: string): string {
  const purposes: Record<string, string> = {
    '__Secure-3PAPISID': 'Google API authentication for third-party services',
    '__Secure-3PSID': 'Google session identification for third-party services',
    '__Secure-3PSIDCC': 'Google session with consent verification (third-party)',
    '__Secure-1PAPISID': 'Google API authentication for first-party services',
    '__Secure-1PSID': 'Google session identification for first-party services',
    '__Secure-1PSIDCC': 'Google session with consent verification (first-party)',
    '__Secure-1PSIDTS': 'Google session timestamp (first-party)',
    '__Secure-3PSIDTS': 'Google session timestamp (third-party)',
    '__Secure-ENID': 'Google Analytics and advertising preferences',
    '__Secure-HSID': 'Google account security and authentication',
    '__Secure-SSID': 'Google account session management',
    '__Secure-APISID': 'Google API session management',
    '__Secure-SAPISID': 'Google secure API session management',
    '__Secure-SID': 'Google session identification',
    '__Secure-SIDCC': 'Google session with consent verification',
    'NID': 'Google preferences and personalization',
    'OGPC': 'Google account preferences',
    'OGP': 'Google account preferences',
    '1P_JAR': 'Google personalization and advertising',
    'AEC': 'Google account security verification',
  };

  return purposes[cookieName] || 'Google service functionality and personalization';
}

/**
 * Get the typical duration of a Google cookie
 */
function getCookieDuration(cookieName: string): string {
  const durations: Record<string, string> = {
    '__Secure-3PAPISID': '2 years',
    '__Secure-3PSID': '2 years',
    '__Secure-3PSIDCC': '2 years',
    '__Secure-1PAPISID': '2 years',
    '__Secure-1PSID': '2 years',
    '__Secure-1PSIDCC': '2 years',
    '__Secure-1PSIDTS': 'Session',
    '__Secure-3PSIDTS': 'Session',
    '__Secure-ENID': '1 year',
    '__Secure-HSID': '2 years',
    '__Secure-SSID': '2 years',
    '__Secure-APISID': '2 years',
    '__Secure-SAPISID': '2 years',
    '__Secure-SID': '2 years',
    '__Secure-SIDCC': '2 years',
    'NID': '6 months',
    'OGPC': '2 years',
    'OGP': '2 years',
    '1P_JAR': '1 month',
    'AEC': '6 months',
  };

  return durations[cookieName] || 'Varies';
}

